/**
 * The radar's components. The palette they use, and why it differs from Role Prioritisation's,
 * lives in `radarStatus.ts`.
 */
import { cn } from '../../../../../shared';
import type { SignalStatus, StageStatus } from '../../../utils/bottleneckRadar';
import { DOT_SIZE, STATUS } from './radarStatus';

export function Dot({
  status,
  size = 'md',
  className,
}: {
  status: StageStatus;
  size?: keyof typeof DOT_SIZE;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn('inline-block flex-none rounded-full', DOT_SIZE[size], STATUS[status].dot, className)}
    />
  );
}

export function StatusPill({ status, label }: { status: StageStatus; label?: string }) {
  return (
    <span
      className={cn(
        'inline-flex flex-none items-center gap-1.5 rounded-full px-2.5 py-0.5',
        'text-xs font-semibold whitespace-nowrap',
        STATUS[status].pill
      )}
    >
      <Dot status={status} size="sm" />
      {label ?? STATUS[status].label}
    </span>
  );
}

/** The role's priority, deliberately colourless here — see the file header. */
export function PriorityChip({ rating }: { rating: string }) {
  return (
    <span
      title="Approved priority rating"
      className="inline-block min-w-[34px] rounded-full border border-solid border-pewter bg-white px-2 py-px text-center text-[11px] font-semibold text-asphalt"
    >
      {rating}
    </span>
  );
}

/** A neutral marker for a role whose pipeline has not had a full cycle yet. */
export function RampChip({ weeks }: { weeks: number }) {
  return (
    <span
      title={`Open ${weeks} week${weeks === 1 ? '' : 's'} — red is softened to amber until the pipeline has had a full cycle`}
      className="inline-block flex-none rounded-full border border-solid border-pearl bg-cotton px-2 py-px text-[11px] font-semibold whitespace-nowrap text-iron"
    >
      Ramp-up
    </span>
  );
}

/**
 * One measure inside a phase.
 *
 * `info` figures are asphalt rather than a status colour — they are measured and shown but do not
 * drive the light. `unknown` figures are steel, so "we cannot judge this" never looks like a pass.
 */
export function SignalRow({
  label,
  value,
  sub,
  status,
}: {
  label: string;
  value: string;
  sub: string;
  status: SignalStatus;
}) {
  const tone =
    status === 'info' ? 'text-asphalt' : status === 'unknown' ? 'text-steel' : STATUS[status].text;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] text-iron">{label}</span>
        <span className={cn('text-[13px] font-semibold tabular-nums', tone)}>{value}</span>
      </div>
      <div className="text-[10px] leading-tight text-steel">{sub}</div>
    </div>
  );
}

/** A KPI tile. Label ABOVE the value — an inline row wraps at real widths and stops being a strip. */
export function KpiTile({
  label,
  value,
  note,
  status,
}: {
  label: string;
  value: string | number;
  note?: string;
  status?: StageStatus;
}) {
  return (
    <div className="rounded-lg bg-white p-4 shadow-card">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-iron">
        {status ? <Dot status={status} size="sm" /> : null}
        {label}
      </div>
      <div
        className={cn(
          'mt-1 truncate text-2xl font-semibold tabular-nums',
          status ? STATUS[status].text : 'text-black'
        )}
        title={String(value)}
      >
        {value}
      </div>
      {note ? <div className="mt-0.5 text-[11px] text-iron">{note}</div> : null}
    </div>
  );
}
